
(() => {
  if (customElements.get('tb-menu-nav')) return;

  class TbMenuNav extends HTMLElement {
    connectedCallback() {
      this.stack = [this.querySelector('.tb-menu-panel[data-panel]')?.dataset.panel || 'root'];
      this.templates = new Map();

      this.querySelectorAll('template[data-panel-template]').forEach((tpl) => {
        this.templates.set(tpl.dataset.panelTemplate, tpl);
      });

      this.addEventListener('click', this._onClick);

      this._dialog = this.closest('dialog');
      if (this._dialog) {
        this._dialog.addEventListener('close', () => {
          this.reset();
          this._releaseSearchResults();
        });
        // Native <dialog> has no "open" event, so watch the `open` attribute
        // directly to know the instant the menu is shown.
        this._dialogOpenObserver = new MutationObserver(() => {
          if (this._dialog.open) {
            this._claimSearchResults();
            // The browser's native <dialog> autofocus (or the theme's own
            // drawer script) can focus the first focusable element inside —
            // which happens to be the search input — the instant the menu
            // opens. That pops the mobile keyboard and shows a blinking
            // cursor before the shopper has tapped anything, which we only
            // want once they actually choose to search. Blur it straight
            // back out.
            const searchInput = this.querySelector('[data-input-search]');
            if (searchInput && document.activeElement === searchInput) {
              searchInput.blur();
            }
          }
        });
        this._dialogOpenObserver.observe(this._dialog, { attributes: true, attributeFilter: ['open'] });
      }

      this._watchSearchResults();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this._searchObserver?.disconnect();
      this._dialogOpenObserver?.disconnect();
    }

    _onClick = (event) => {
      const openBtn = event.target.closest('[data-menu-open]');
      if (openBtn) {
        event.preventDefault();
        this.open(openBtn.dataset.menuOpen);
        return;
      }
      const backBtn = event.target.closest('[data-menu-back]');
      if (backBtn) {
        event.preventDefault();
        this.back();
        return;
      }
      const crumbBtn = event.target.closest('[data-menu-jump]');
      if (crumbBtn) {
        event.preventDefault();
        this.jumpTo(crumbBtn.dataset.menuJump);
      }
    };

    getPanel(id) {
      return this.querySelector(`.tb-menu-panel[data-panel="${CSS.escape(id)}"]`);
    }

    hydrate(id) {
      let panel = this.getPanel(id);
      if (panel) return panel;
      const tpl = this.templates.get(id);
      if (!tpl) return null;
      this.appendChild(tpl.content.cloneNode(true));
      this.querySelectorAll('template[data-panel-template]').forEach((t) => {
        if (!this.templates.has(t.dataset.panelTemplate)) {
          this.templates.set(t.dataset.panelTemplate, t);
        }
      });
      return this.getPanel(id);
    }

    open(id) {
      if (!id || this.stack[this.stack.length - 1] === id) return;
      const target = this.hydrate(id);
      if (!target) return;
      const current = this.getPanel(this.stack[this.stack.length - 1]);
      this.stack.push(id);
      this._transition(current, target, 'forward');
      this._renderBreadcrumb();
    }

    back() {
      if (this.stack.length <= 1) return;
      const fromId = this.stack.pop();
      const toId = this.stack[this.stack.length - 1];
      this._transition(this.getPanel(fromId), this.getPanel(toId), 'back');
      this._renderBreadcrumb();
    }

    jumpTo(id) {
      const idx = this.stack.indexOf(id);
      if (idx === -1 || idx === this.stack.length - 1) return;
      const fromId = this.stack[this.stack.length - 1];
      this.stack = this.stack.slice(0, idx + 1);
      this._transition(this.getPanel(fromId), this.getPanel(id), 'back');
      this._renderBreadcrumb();
    }

    reset() {
      if (this.stack.length <= 1) return;
      const rootId = this.stack[0];
      this.stack.slice(1).forEach((id) => {
        const el = this.getPanel(id);
        el?.classList.remove('is-active', 'is-incoming-forward', 'is-incoming-back', 'is-outgoing-forward', 'is-outgoing-back');
      });
      this.getPanel(rootId)?.classList.add('is-active');
      this.stack = [rootId];
    }

    // Breadcrumb = "Home" (jumps to the root menu) followed by the title of
    // every panel in the stack above the root, ending with the current
    // panel's own title (non-clickable). Shown on every drilldown screen,
    // starting from the very first one (e.g. "Home > Shop Hampers"), growing
    // as you go deeper (e.g. "Home > Shop Hampers > By Type").
    _renderBreadcrumb() {
      const currentId = this.stack[this.stack.length - 1];
      const currentPanel = this.getPanel(currentId);
      const holder = currentPanel?.querySelector('[data-menu-breadcrumb]');
      if (!holder) return;

      const crumbIds = this.stack.slice(1); // exclude the root panel itself
      if (crumbIds.length < 1) {
        holder.innerHTML = '';
        return;
      }

      const esc = (str) => String(str).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));

      const rootId = this.stack[0];
      const parts = [
        `<button type="button" class="tb-menu-breadcrumb__link" data-menu-jump="${esc(rootId)}">Home</button><span class="tb-menu-breadcrumb__sep" aria-hidden="true">\u276F</span>`,
      ];

      crumbIds.forEach((id, i) => {
        const panel = this.getPanel(id);
        const title = panel?.querySelector('.tb-menu-panel__banner-title')?.textContent?.trim() || '';
        if (i === crumbIds.length - 1) {
          parts.push(`<span class="tb-menu-breadcrumb__current">${esc(title)}</span>`);
        } else {
          parts.push(`<button type="button" class="tb-menu-breadcrumb__link" data-menu-jump="${esc(id)}">${esc(title)}</button><span class="tb-menu-breadcrumb__sep" aria-hidden="true">\u276F</span>`);
        }
      });

      holder.innerHTML = parts.join('');
    }

    // Relocate Snize's results dropdown into this dialog — but ONLY while the
    // menu is actually open, and only for the shared results element itself.
    // Snize uses one shared dropdown for every search input on the site
    // (this menu's, the header's, etc.), so grabbing it unconditionally would
    // strand it inside this (closed) dialog and break search everywhere else
    // on the page. It's returned to <body> as soon as the menu closes.
    _watchSearchResults() {
      this._searchObserver = new MutationObserver(() => this._claimSearchResults());
      this._searchObserver.observe(document.body, { childList: true, subtree: true });
    }

    _findSearchResultsEl() {
      return document.getElementById('snize-instant-search-results')
        || document.querySelector('.snize-ac-results');
    }

    _claimSearchResults() {
      if (!this._dialog?.open) return;
      const searchWrap = this.querySelector('.tb-menu-search');
      const el = this._findSearchResultsEl();
      if (!searchWrap || !el || searchWrap.contains(el)) return;
      searchWrap.appendChild(el);
      el.classList.add('tb-menu-search__portal-results');
    }

    _releaseSearchResults() {
      const searchWrap = this.querySelector('.tb-menu-search');
      const el = this._findSearchResultsEl();
      if (!searchWrap || !el || !searchWrap.contains(el)) return;
      el.classList.remove('tb-menu-search__portal-results');
      document.body.appendChild(el);
    }

    _transition(fromEl, toEl, direction) {
      if (!toEl || toEl === fromEl) return;
      this.classList.add('is-animating');
      toEl.classList.add(direction === 'forward' ? 'is-incoming-forward' : 'is-incoming-back');
      toEl.classList.add('is-active');
      const body = toEl.querySelector('.tb-menu-panel__body');
      if (body) body.scrollTop = 0;

      requestAnimationFrame(() => {
        toEl.classList.remove('is-incoming-forward', 'is-incoming-back');
        if (fromEl) {
          fromEl.classList.remove('is-active');
          fromEl.classList.add(direction === 'forward' ? 'is-outgoing-forward' : 'is-outgoing-back');
        }
      });

      const cleanup = () => {
        fromEl?.classList.remove('is-outgoing-forward', 'is-outgoing-back');
        this.classList.remove('is-animating');
        toEl.removeEventListener('transitionend', cleanup);
      };
      toEl.addEventListener('transitionend', cleanup);
      setTimeout(cleanup, 400); // safety net if transitionend doesn't fire
    }
  }

  customElements.define('tb-menu-nav', TbMenuNav);

  // KSP Discount Codes Click-to-Copy
  function copyKspDiscount(text) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => fallbackKspCopy(text));
    } else {
      fallbackKspCopy(text);
    }
  }

  function fallbackKspCopy(text) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      console.warn('KSP copy error:', e);
    }
  }

  document.addEventListener('click', (event) => {
    const item = event.target.closest('.tb-menu-ksp__item');
    if (!item) return;

    event.preventDefault();
    const code = item.dataset.code;
    if (!code) return;

    copyKspDiscount(code);

    if (navigator.vibrate) {
      try { navigator.vibrate(25); } catch (_) {}
    }

    document.querySelectorAll('.tb-menu-ksp__item').forEach(btn => btn.classList.remove('is-copied'));
    item.classList.add('is-copied');

    clearTimeout(item._copyTimeout);
    item._copyTimeout = setTimeout(() => {
      item.classList.remove('is-copied');
    }, 1800);
  });
})();

document.addEventListener("DOMContentLoaded", function() {
    document.querySelectorAll('.tb-menu-row__title').forEach(title => {
        if (title.textContent.trim() === 'Christmas Hampers') {
            title.style.setProperty('color', '#ec5e2a', 'important');
            
            let subtitle = title.nextElementSibling;
            if (subtitle && subtitle.classList.contains('tb-menu-row__subtitle')) {
                subtitle.style.setProperty('color', '#ec5e2a', 'important');
            }
        }
    });
});